import { graphqlRequest } from "../client";
import { EntityStatus } from "../../types";
import type {
  CreateAppConfigurationInput,
  CreateEnvironmentInput,
  CreateManagedApplicationInput,
  CreateSoftwareReleaseInput,
  DevopsApplication,
  DevopsConfiguration,
  DevopsEnvironment,
  DevopsRelease,
} from "../../types/devops";

const ENV_FIELDS = `
  uuid tenantId name description status tags createdBy createdOn
  purpose dataSensitivity owner managers sharedConfig
  platformServices { type name endpoint notes }
  composition { regions clusters notes }
  applications { uuid name }
`;

const APP_FIELDS = `
  uuid tenantId name description status tags createdBy createdOn
  applicationKind deploymentType sourceRepository owner managers
  environments { uuid name }
  dependsOn { uuid name }
`;

const CFG_FIELDS = `
  uuid tenantId name description status tags createdBy createdOn
  scope format content owner managers
  environment { uuid name }
  applications { uuid name }
  dependsOn { uuid name }
`;

const REL_FIELDS = `
  uuid tenantId name description status tags createdBy createdOn
  version releaseStatus changelog startedOn endedOn verifiedBy signedOffBy
  owner managers
  environment { uuid name }
  applications { uuid name }
  configurations { uuid name }
  changeManagementRefs { system ticketId url }
  documentationLinks { title url }
`;

export const devopsService = {
  async listEnvironments(): Promise<DevopsEnvironment[]> {
    const data = await graphqlRequest<{ environments: DevopsEnvironment[] }>(
      `query { environments { ${ENV_FIELDS} } }`,
    );
    return data.environments;
  },

  async getEnvironment(uuid: string): Promise<DevopsEnvironment | null> {
    const data = await graphqlRequest<{ environment: DevopsEnvironment | null }>(
      `query ($uuid: ID!) { environment(uuid: $uuid) { ${ENV_FIELDS} } }`,
      { uuid },
    );
    return data.environment;
  },

  async createEnvironment(input: CreateEnvironmentInput): Promise<DevopsEnvironment> {
    const data = await graphqlRequest<{ createEnvironment: DevopsEnvironment }>(
      `mutation ($input: CreateEnvironmentInput!) { createEnvironment(input: $input) { ${ENV_FIELDS} } }`,
      { input },
    );
    return data.createEnvironment;
  },

  async updateEnvironment(
    uuid: string,
    input: Partial<CreateEnvironmentInput> & { status?: EntityStatus },
  ): Promise<DevopsEnvironment> {
    const data = await graphqlRequest<{ updateEnvironment: DevopsEnvironment }>(
      `mutation ($uuid: ID!, $input: UpdateEnvironmentInput!) { updateEnvironment(uuid: $uuid, input: $input) { ${ENV_FIELDS} } }`,
      { uuid, input },
    );
    return data.updateEnvironment;
  },

  async deleteEnvironment(uuid: string): Promise<boolean> {
    const data = await graphqlRequest<{ deleteEnvironment: boolean }>(
      `mutation ($uuid: ID!) { deleteEnvironment(uuid: $uuid) }`,
      { uuid },
    );
    return data.deleteEnvironment;
  },

  async linkEnvironmentApplication(
    environmentUuid: string,
    applicationUuid: string,
  ): Promise<DevopsEnvironment> {
    const data = await graphqlRequest<{ linkEnvironmentApplication: DevopsEnvironment }>(
      `mutation ($environmentUuid: ID!, $applicationUuid: ID!) {
        linkEnvironmentApplication(environmentUuid: $environmentUuid, applicationUuid: $applicationUuid) { ${ENV_FIELDS} }
      }`,
      { environmentUuid, applicationUuid },
    );
    return data.linkEnvironmentApplication;
  },

  async unlinkEnvironmentApplication(
    environmentUuid: string,
    applicationUuid: string,
  ): Promise<DevopsEnvironment> {
    const data = await graphqlRequest<{ unlinkEnvironmentApplication: DevopsEnvironment }>(
      `mutation ($environmentUuid: ID!, $applicationUuid: ID!) {
        unlinkEnvironmentApplication(environmentUuid: $environmentUuid, applicationUuid: $applicationUuid) { ${ENV_FIELDS} }
      }`,
      { environmentUuid, applicationUuid },
    );
    return data.unlinkEnvironmentApplication;
  },

  async listApplications(): Promise<DevopsApplication[]> {
    const data = await graphqlRequest<{ managedApplications: DevopsApplication[] }>(
      `query { managedApplications { ${APP_FIELDS} } }`,
    );
    return data.managedApplications;
  },

  async getApplication(uuid: string): Promise<DevopsApplication | null> {
    const data = await graphqlRequest<{ managedApplication: DevopsApplication | null }>(
      `query ($uuid: ID!) { managedApplication(uuid: $uuid) { ${APP_FIELDS} } }`,
      { uuid },
    );
    return data.managedApplication;
  },

  async createApplication(input: CreateManagedApplicationInput): Promise<DevopsApplication> {
    const data = await graphqlRequest<{ createManagedApplication: DevopsApplication }>(
      `mutation ($input: CreateManagedApplicationInput!) { createManagedApplication(input: $input) { ${APP_FIELDS} } }`,
      { input },
    );
    return data.createManagedApplication;
  },

  async updateApplication(
    uuid: string,
    input: Partial<CreateManagedApplicationInput> & { status?: EntityStatus },
  ): Promise<DevopsApplication> {
    const data = await graphqlRequest<{ updateManagedApplication: DevopsApplication }>(
      `mutation ($uuid: ID!, $input: UpdateManagedApplicationInput!) {
        updateManagedApplication(uuid: $uuid, input: $input) { ${APP_FIELDS} }
      }`,
      { uuid, input },
    );
    return data.updateManagedApplication;
  },

  async deleteApplication(uuid: string): Promise<boolean> {
    const data = await graphqlRequest<{ deleteManagedApplication: boolean }>(
      `mutation ($uuid: ID!) { deleteManagedApplication(uuid: $uuid) }`,
      { uuid },
    );
    return data.deleteManagedApplication;
  },

  async getConfiguration(uuid: string): Promise<DevopsConfiguration | null> {
    const data = await graphqlRequest<{ appConfiguration: DevopsConfiguration | null }>(
      `query ($uuid: ID!) { appConfiguration(uuid: $uuid) { ${CFG_FIELDS} } }`,
      { uuid },
    );
    return data.appConfiguration;
  },

  async listConfigurations(environmentUuid?: string): Promise<DevopsConfiguration[]> {
    const data = await graphqlRequest<{ appConfigurations: DevopsConfiguration[] }>(
      `query ($environmentUuid: ID) { appConfigurations(environmentUuid: $environmentUuid) { ${CFG_FIELDS} } }`,
      { environmentUuid: environmentUuid ?? null },
    );
    return data.appConfigurations;
  },

  async createConfiguration(input: CreateAppConfigurationInput): Promise<DevopsConfiguration> {
    const data = await graphqlRequest<{ createAppConfiguration: DevopsConfiguration }>(
      `mutation ($input: CreateAppConfigurationInput!) { createAppConfiguration(input: $input) { ${CFG_FIELDS} } }`,
      { input },
    );
    return data.createAppConfiguration;
  },

  async updateConfiguration(
    uuid: string,
    input: { name?: string; content?: string; status?: EntityStatus },
  ): Promise<DevopsConfiguration> {
    const data = await graphqlRequest<{ updateAppConfiguration: DevopsConfiguration }>(
      `mutation ($uuid: ID!, $input: UpdateAppConfigurationInput!) {
        updateAppConfiguration(uuid: $uuid, input: $input) { ${CFG_FIELDS} }
      }`,
      { uuid, input },
    );
    return data.updateAppConfiguration;
  },

  async deleteConfiguration(uuid: string): Promise<boolean> {
    const data = await graphqlRequest<{ deleteAppConfiguration: boolean }>(
      `mutation ($uuid: ID!) { deleteAppConfiguration(uuid: $uuid) }`,
      { uuid },
    );
    return data.deleteAppConfiguration;
  },

  async getRelease(uuid: string): Promise<DevopsRelease | null> {
    const data = await graphqlRequest<{ softwareRelease: DevopsRelease | null }>(
      `query ($uuid: ID!) { softwareRelease(uuid: $uuid) { ${REL_FIELDS} } }`,
      { uuid },
    );
    return data.softwareRelease;
  },

  async listReleases(environmentUuid?: string): Promise<DevopsRelease[]> {
    const data = await graphqlRequest<{ softwareReleases: DevopsRelease[] }>(
      `query ($environmentUuid: ID) { softwareReleases(environmentUuid: $environmentUuid) { ${REL_FIELDS} } }`,
      { environmentUuid: environmentUuid ?? null },
    );
    return data.softwareReleases;
  },

  async createRelease(input: CreateSoftwareReleaseInput): Promise<DevopsRelease> {
    const data = await graphqlRequest<{ createSoftwareRelease: DevopsRelease }>(
      `mutation ($input: CreateSoftwareReleaseInput!) { createSoftwareRelease(input: $input) { ${REL_FIELDS} } }`,
      { input },
    );
    return data.createSoftwareRelease;
  },

  async updateRelease(
    uuid: string,
    input: Partial<CreateSoftwareReleaseInput> & { status?: EntityStatus },
  ): Promise<DevopsRelease> {
    const data = await graphqlRequest<{ updateSoftwareRelease: DevopsRelease }>(
      `mutation ($uuid: ID!, $input: UpdateSoftwareReleaseInput!) {
        updateSoftwareRelease(uuid: $uuid, input: $input) { ${REL_FIELDS} }
      }`,
      { uuid, input },
    );
    return data.updateSoftwareRelease;
  },

  async deleteRelease(uuid: string): Promise<boolean> {
    const data = await graphqlRequest<{ deleteSoftwareRelease: boolean }>(
      `mutation ($uuid: ID!) { deleteSoftwareRelease(uuid: $uuid) }`,
      { uuid },
    );
    return data.deleteSoftwareRelease;
  },
};
